import { createContext, useContext, useEffect, useState } from 'react';
import { Box, chakra, Flex, Text } from '@chakra-ui/react';
import { defaultSiteContent, type SiteContent } from '@denise-augusta/domain';
import { fetchPublishedContent } from '@/lib/blob-stores';

const SiteContentContext = createContext<SiteContent>(defaultSiteContent);

const PREVIEW_KEY = 'da-content-preview';

/**
 * Draft preview mode: entering the site with ?preview=draft turns it on for
 * the whole tab (it survives client-side navigation); any other ?preview
 * value (e.g. ?preview=live) or the banner's Exit button turns it off.
 * NOTE: preview links must be full page loads (<a>, not router links) —
 * this resolves once when the app boots.
 */
function resolvePreviewMode(): boolean {
  const param = new URLSearchParams(window.location.search).get('preview');
  if (param === 'draft') {
    sessionStorage.setItem(PREVIEW_KEY, 'draft');
    return true;
  }
  if (param !== null) {
    sessionStorage.removeItem(PREVIEW_KEY);
    return false;
  }
  return sessionStorage.getItem(PREVIEW_KEY) === 'draft';
}

/**
 * Provides the site content to every public page: the baked-in defaults
 * render immediately, then the published document from blob storage (or the
 * working draft, in preview mode) swaps in.
 */
export function SiteContentProvider({ children }: { children: React.ReactNode }) {
  const [content, setContent] = useState<SiteContent>(defaultSiteContent);
  const [preview] = useState<boolean>(resolvePreviewMode);
  const [draftMissing, setDraftMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (preview) {
        const draft = await fetchPublishedContent('draft');
        if (draft && !cancelled) {
          setContent(draft);
          return;
        }
        if (!cancelled) setDraftMissing(true);
      }
      const published = await fetchPublishedContent('published');
      if (published && !cancelled) setContent(published);
    })();
    return () => {
      cancelled = true;
    };
  }, [preview]);

  return (
    <SiteContentContext.Provider value={content}>
      {preview && <DraftBanner draftMissing={draftMissing} />}
      {children}
    </SiteContentContext.Provider>
  );
}

function DraftBanner({ draftMissing }: { draftMissing: boolean }) {
  const exit = () => {
    sessionStorage.removeItem(PREVIEW_KEY);
    const url = new URL(window.location.href);
    url.searchParams.delete('preview');
    window.location.href = url.toString();
  };

  return (
    <Flex
      position="fixed"
      bottom="0"
      left="0"
      right="0"
      zIndex="400"
      bg="brand.terracotta"
      color="paper.white"
      px="1.2rem"
      py="0.55rem"
      align="center"
      justify="center"
      gap="1rem"
    >
      <Text fontSize="0.72rem" fontWeight="600" letterSpacing="0.12em" textTransform="uppercase">
        {draftMissing ? 'Draft preview — no draft found, showing published' : 'Draft preview — not the live site'}
      </Text>
      <chakra.button
        onClick={exit}
        bg="rgba(255,255,255,0.18)"
        border="1px solid rgba(255,255,255,0.5)"
        color="paper.white"
        fontSize="0.66rem"
        fontWeight="600"
        letterSpacing="0.1em"
        textTransform="uppercase"
        px="0.9rem"
        py="0.3rem"
        borderRadius="14px"
        cursor="pointer"
        _hover={{ bg: 'rgba(255,255,255,0.3)' }}
      >
        Exit Preview
      </chakra.button>
    </Flex>
  );
}

export function useSiteContent(): SiteContent {
  return useContext(SiteContentContext);
}
